import { memo, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ArrowLeft, Share2, Eye, Save, Trophy, Loader2 } from "lucide-react";
import { Button } from "./Button";
import { ScoreInput } from "./ScoreInput";
import { MoodPicker } from "./MoodPicker";
import { PredictionCard } from "./PredictionCard";
import { ShareModal } from "./ShareModal";
import { UserMetrics } from "./UserMetrics";
import { FirstPredictionInfoModal } from "./FirstPredictionInfoModal";
// import { BannerAd } from "./BannerAd";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { usePredictions } from "../hooks/usePredictions";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";
import { useToast } from "../contexts/ToastContext";
import { trackPredictionMade } from "../lib/analytics";
import { formatMatchTime } from "../lib/dateUtils";
import type { Screen, Mood, Prediction, Match } from "../types";

interface PredictorScreenProps {
  match: Match;
  onBack: () => void;
  onNavigate: (screen: Exclude<Screen, "predictor">) => void;
  // onShowAd?: () => void;
}

export const PredictorScreen = memo(function PredictorScreen({
  match,
  onBack,
  onNavigate,
}: // onShowAd,
PredictorScreenProps) {
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [mood, setMood] = useState<Mood>("confident");
  const [showPreview, setShowPreview] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSavedForStartedMatch, setJustSavedForStartedMatch] =
    useState(false);
  const [showFirstPredictionModal, setShowFirstPredictionModal] =
    useState(false);
  const shareModalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const savedStateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { showToast } = useToast();
  const { t, language } = useLanguage();
  const { user, profile, refreshProfile } = useAuth();
  const { getCountryName } = useCountryTranslations();

  const translatedHomeTeam = getCountryName(match.homeTeam);
  const translatedAwayTeam = getCountryName(match.awayTeam);
  const {
    predictions: recentPredictions,
    userPrediction,
    loading: predictionsLoading,
    createPrediction,
  } = usePredictions(match.id, user?.id);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (shareModalTimeoutRef.current) {
        clearTimeout(shareModalTimeoutRef.current);
      }
      if (savedStateTimeoutRef.current) {
        clearTimeout(savedStateTimeoutRef.current);
      }
    };
  }, []);

  // Load existing prediction if user has one
  useEffect(() => {
    if (userPrediction) {
      setHomeScore(userPrediction.home_score);
      setAwayScore(userPrediction.away_score);
      setMood(userPrediction.mood);
      setShowPreview(true); // Show preview automatically when prediction exists
      // Reset justSavedForStartedMatch when prediction is loaded from the hook
      // (this happens when the prediction is created and the hook refetches)
      if (match.status === "live" || match.status === "completed") {
        setJustSavedForStartedMatch(true);
      }
    }
  }, [userPrediction, match.status]);

  const handleSave = useCallback(async () => {
    if (!user) {
      // Show login modal or alert
      return;
    }

    // Check if user needs to provide country (check on every prediction attempt)
    if (profile && !profile.supported_country) {
      setShowFirstPredictionModal(true);
      return;
    }

    setSaving(true);
    try {
      const prediction: Prediction = {
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        homeFlag: match.homeFlag,
        awayFlag: match.awayFlag,
        homeScore,
        awayScore,
        mood,
      };

      const { error } = await createPrediction(match.id, prediction, user.id);

      if (error) {
        showToast(error.message || t("common.unexpectedError"), "error");
        setSaving(false);
        return;
      }

      // Refresh profile to update metrics
      await refreshProfile();

      // Track prediction made
      trackPredictionMade(match.id, match.homeTeam, match.awayTeam);

      // Show success toast
      setSaved(true);
      setShowPreview(true);
      showToast(t("predictor.success"), "success");

      // Automatically show share modal after successful prediction
      // This encourages users to share and compete with friends
      shareModalTimeoutRef.current = setTimeout(() => {
        setShowShareModal(true);
        shareModalTimeoutRef.current = null;
      }, 800);

      // Show full-screen ad after saving prediction (with delay)
      // if (onShowAd) {
      //   setTimeout(() => {
      //     onShowAd();
      //   }, 1500);
      // }

      // If match has started (live or completed), immediately disable the button and mark as predicted
      if (match.status === "live" || match.status === "completed") {
        setJustSavedForStartedMatch(true);
      } else {
        // For upcoming matches, reset the saved state after 2 seconds
        savedStateTimeoutRef.current = setTimeout(() => {
          setSaved(false);
          savedStateTimeoutRef.current = null;
        }, 2000);
      }
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : t("common.unexpectedError"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [
    user,
    profile,
    match,
    homeScore,
    awayScore,
    mood,
    createPrediction,
    refreshProfile,
    t,
    showToast,
    // onShowAd,
  ]);

  const handleShare = useCallback(() => {
    setShowShareModal(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);

  const proceedWithSave = useCallback(async () => {
    if (!user) return;

    setSaving(true);
    try {
      const prediction: Prediction = {
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        homeFlag: match.homeFlag,
        awayFlag: match.awayFlag,
        homeScore,
        awayScore,
        mood,
      };

      const { error } = await createPrediction(match.id, prediction, user.id);

      if (error) {
        showToast(error.message || t("common.unexpectedError"), "error");
        setSaving(false);
        return;
      }

      await refreshProfile();
      trackPredictionMade(match.id, match.homeTeam, match.awayTeam);
      setSaved(true);
      setShowPreview(true);
      showToast(t("predictor.success"), "success");

      shareModalTimeoutRef.current = setTimeout(() => {
        setShowShareModal(true);
        shareModalTimeoutRef.current = null;
      }, 800);

      if (match.status === "live" || match.status === "completed") {
        setJustSavedForStartedMatch(true);
      } else {
        savedStateTimeoutRef.current = setTimeout(() => {
          setSaved(false);
          savedStateTimeoutRef.current = null;
        }, 2000);
      }
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : t("common.unexpectedError"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [
    user,
    match,
    homeScore,
    awayScore,
    mood,
    createPrediction,
    refreshProfile,
    showToast,
    t,
  ]);

  const handleFirstPredictionModalComplete = useCallback(async () => {
    // After user completes the profile info, proceed with saving
    setShowFirstPredictionModal(false);
    // Wait for profile to refresh, then proceed with save
    await refreshProfile();
    proceedWithSave();
  }, [refreshProfile, proceedWithSave]);

  const handleFirstPredictionModalSkip = useCallback(() => {
    // User skipped - still allow prediction but show modal again next time
    setShowFirstPredictionModal(false);
    proceedWithSave();
  }, [proceedWithSave]);

  const togglePreview = useCallback(() => {
    setShowPreview((prev) => !prev);
  }, []);

  const prediction = useMemo(
    () => ({
      homeTeam: match.homeTeam,
      awayTeam: match.awayTeam,
      homeFlag: match.homeFlag,
      awayFlag: match.awayFlag,
      homeScore,
      awayScore,
      mood,
      status: match.status,
    }),
    [match, homeScore, awayScore, mood]
  );

  const mappedRecentPredictions = useMemo(() => {
    if (predictionsLoading || !recentPredictions.length) return [];

    // Map predictions to display format with actual user names
    return recentPredictions.slice(0, 3).map((pred) => ({
      name: pred.userName || "User",
      score: `${pred.homeScore}-${pred.awayScore}`,
      mood:
        pred.mood === "confident"
          ? "😎"
          : pred.mood === "nervous"
          ? "😰"
          : "😈",
      points: pred.userTotalPoints || 0,
    }));
  }, [recentPredictions, predictionsLoading]);

  const hasExistingPrediction = !!userPrediction;

  // Disable button if:
  // 1. Currently saving
  // 2. No user logged in
  // 3. Just saved for a match that has started (live/completed)
  // Note: For upcoming matches, users can always modify their prediction
  const isSaveButtonDisabled = saving || !user || justSavedForStartedMatch;

  // Show "predicted" state if:
  // - Just saved for a started match, OR
  // - Has existing prediction and match has started
  const showPredictedState =
    justSavedForStartedMatch ||
    (hasExistingPrediction &&
      (match.status === "live" || match.status === "completed"));

  // Disable inputs if:
  // - Match has started (live/completed) and user has a prediction or just saved
  // Note: For upcoming matches, inputs are always enabled to allow modifications
  const areInputsDisabled = showPredictedState;

  return (
    <div className="min-h-screen bg-slate-50 gradient-mesh">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={onBack}
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
          >
            <ArrowLeft
              className={`w-5 h-5 text-slate-600 ${
                language === "ar" ? "rotate-180" : ""
              }`}
            />
          </button>
          <span className="text-slate-900 font-semibold">
            {t("predictor.title")}
          </span>
          <button
            onClick={() => onNavigate("leaderboard")}
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
          >
            <Trophy className="w-5 h-5 text-primary" />
          </button>
        </div>
      </div>

      <main className="max-w-md mx-auto w-full px-4 py-6">
        {/* User Metrics */}
        <div className="mb-4">
          <UserMetrics variant="compact" />
        </div>

        {/* Match Info */}
        <div className="bg-white rounded-3xl shadow-md p-6 mb-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="text-center text-sm text-slate-600 font-medium bg-slate-50 px-4 py-2 rounded-full inline-block">
              {formatMatchTime(match.matchTime, language)}
            </div>
            {match.status === "live" && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-red-700 rounded-full animate-pulse"></div>
                <span className="px-3 py-1.5 bg-red-700 text-white rounded-full text-xs font-semibold">
                  {t("match.live")}
                </span>
              </div>
            )}
            {match.status === "completed" && (
              <span className="px-3 py-1.5 bg-slate-400 text-white rounded-full text-xs font-semibold">
                {t("match.fullTime")}
              </span>
            )}
          </div>
          <div className="flex items-center justify-center gap-5">
            <div className="text-center flex-1">
              <div className="text-5xl mb-3">{match.homeFlag}</div>
              <div className="text-sm font-semibold text-slate-900">
                {translatedHomeTeam}
              </div>
            </div>
            <div className="px-4 py-2.5 bg-gradient-to-br from-primary-lighter to-primary-light rounded-xl text-primary font-semibold text-sm border border-primary/10">
              {t("match.vs")}
            </div>
            <div className="text-center flex-1">
              <div className="text-5xl mb-3">{match.awayFlag}</div>
              <div className="text-sm font-semibold text-slate-900">
                {translatedAwayTeam}
              </div>
            </div>
          </div>
        </div>

        {/* Banner Ad - After Match Info */}
        {/* <div className="mb-6">
          <BannerAd size="small" variant="subtle" />
        </div> */}

        {/* Preview Card */}
        {showPreview && (
          <div className="mb-6 animate-scaleIn">
            <PredictionCard
              homeTeam={match.homeTeam}
              awayTeam={match.awayTeam}
              homeFlag={match.homeFlag}
              awayFlag={match.awayFlag}
              homeScore={homeScore}
              awayScore={awayScore}
              mood={mood}
              status={match.status}
            />
          </div>
        )}

        {/* Score Inputs */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-md p-4 sm:p-5 md:p-6 mb-6 border border-slate-200">
          <h2 className="mb-3 sm:mb-4 md:mb-5 text-base sm:text-lg text-slate-900 font-semibold">
            {t("predictor.predictScore")}
          </h2>
          {hasExistingPrediction && match.status === "upcoming" && (
            <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-primary-lighter border border-primary/20 rounded-lg sm:rounded-xl">
              <p className="text-xs sm:text-sm text-primary font-medium">
                ✓ {t("common.predictionSaved")} - {t("predictor.youCanModify")}
              </p>
            </div>
          )}
          {showPredictedState && (
            <div className="mb-3 sm:mb-4 p-2.5 sm:p-3 bg-slate-100 border border-slate-300 rounded-lg sm:rounded-xl">
              <p className="text-xs sm:text-sm text-slate-600 font-medium">
                ✓ {t("common.predictionSaved")}
              </p>
            </div>
          )}
          <div className="mb-6">
            {/* Team Names with VS */}
            <div className="relative flex items-center justify-center mb-4">
              <div className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xl sm:text-2xl md:text-3xl">
                  {match.homeFlag}
                </span>
                <span className="text-xs sm:text-sm text-slate-600 font-semibold text-center px-6">
                  {translatedHomeTeam}
                </span>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 px-2 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
                <span className="text-slate-700 font-semibold text-xs">
                  {t("match.vs")}
                </span>
              </div>
              <div className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xl sm:text-2xl md:text-3xl">
                  {match.awayFlag}
                </span>
                <span className="text-xs sm:text-sm text-slate-600 font-semibold text-center px-6">
                  {translatedAwayTeam}
                </span>
              </div>
            </div>
            {/* Score Inputs */}
            <div className="flex gap-2 sm:gap-3 md:gap-4">
              <ScoreInput
                value={homeScore}
                onChange={setHomeScore}
                teamName={match.homeTeam}
                teamFlag={match.homeFlag}
                disabled={areInputsDisabled}
              />
              <ScoreInput
                value={awayScore}
                onChange={setAwayScore}
                teamName={match.awayTeam}
                teamFlag={match.awayFlag}
                disabled={areInputsDisabled}
              />
            </div>
          </div>

          <MoodPicker
            selected={mood}
            onChange={setMood}
            disabled={areInputsDisabled}
          />
        </div>

        {/* Mini Leaderboard */}
        <div className="bg-white rounded-3xl shadow-md p-6 mb-6 border border-slate-200">
          <h3 className="text-sm text-slate-900 mb-5 font-semibold uppercase tracking-wide">
            {t("predictor.recent")}
          </h3>
          <div className="space-y-3">
            {predictionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>
            ) : mappedRecentPredictions.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                {t("common.noPredictionsMatch")}
              </div>
            ) : (
              mappedRecentPredictions.map((player, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between py-3 border-b border-slate-200 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl bg-slate-50 w-10 h-10 rounded-xl flex items-center justify-center">
                      {player.mood}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {player.name}
                      </div>
                      <div className="text-xs text-slate-400 font-medium">
                        {player.score}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-gradient-to-br from-primary-lighter to-primary-light px-3 py-2 rounded-xl">
                    <Trophy className="w-3.5 h-3.5 text-primary" />
                    <span className="text-sm text-primary font-semibold">
                      {player.points}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button variant="secondary" fullWidth onClick={togglePreview}>
            <div className="flex items-center justify-center gap-2">
              <Eye className="w-5 h-5" />
              <span>
                {showPreview
                  ? t("predictor.hidePreview")
                  : t("predictor.showPreview")}
              </span>
            </div>
          </Button>

          <div className="grid grid-cols-2 gap-3">
            <Button variant="secondary" onClick={handleShare}>
              <div className="flex items-center justify-center gap-2">
                <Share2 className="w-4 h-4" />
                <span>{t("predictor.share")}</span>
              </div>
            </Button>
            <Button
              variant={showPredictedState ? "secondary" : "primary"}
              onClick={handleSave}
              disabled={isSaveButtonDisabled}
              className={
                showPredictedState ? "opacity-60 cursor-not-allowed" : ""
              }
            >
              <div className="flex items-center justify-center gap-2">
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>
                  {saving
                    ? t("common.saving")
                    : showPredictedState
                    ? t("predictor.saved")
                    : saved
                    ? t("predictor.saved")
                    : !user
                    ? t("common.signInToSave")
                    : t("predictor.save")}
                </span>
              </div>
            </Button>
          </div>
        </div>
      </main>

      {showShareModal && (
        <ShareModal onClose={handleCloseShareModal} prediction={prediction} />
      )}

      {showFirstPredictionModal && (
        <FirstPredictionInfoModal
          isOpen={showFirstPredictionModal}
          onClose={() => setShowFirstPredictionModal(false)}
          onComplete={handleFirstPredictionModalComplete}
          onSkip={handleFirstPredictionModalSkip}
        />
      )}
    </div>
  );
});
